import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Share2, PlusSquare, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'sta_install_dismissed_at';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari exposes navigator.standalone when launched from home screen
  return !!window.navigator?.standalone;
}

function isIOS() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function InstallPWAButton({ variant = 'default', size = 'sm', className = '', label = 'Install app' }) {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault(); // we'll call prompt() ourselves on click
      setPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onInstallClick = useCallback(async () => {
    if (prompt) {
      prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice?.outcome === 'dismissed') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
      setPrompt(null);
      return;
    }
    // No native prompt available — most commonly iOS Safari or already-installed.
    setIosHelpOpen(true);
  }, [prompt]);

  // Hide completely once installed
  if (installed) return null;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={onInstallClick}
        className={`gap-1.5 ${className}`}
        data-testid="install-pwa-button"
        title="Install StudyTrack AI as an app on this device"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">Install</span>
      </Button>

      {/* Instructions dialog — for iOS or when the browser hasn't fired beforeinstallprompt */}
      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-red-500" />
              Add StudyTrack AI to your home screen
            </DialogTitle>
            <DialogDescription>
              Install the app on this device for faster launches, full-screen study mode and quick re-opens.
            </DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">1</span>
                <span>
                  Tap the <strong>Share</strong> icon
                  <Share2 className="mx-1 inline h-4 w-4 align-text-bottom" />
                  at the bottom of Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">2</span>
                <span>
                  Scroll and tap <strong>Add to Home Screen</strong>
                  <PlusSquare className="mx-1 inline h-4 w-4 align-text-bottom" />.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">3</span>
                <span>Tap <strong>Add</strong> — the StudyTrack icon appears on your home screen.</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">1</span>
                <span>
                  Open the <strong>browser menu</strong> (⋮ on Android · ⋯ on desktop Chrome / Edge).
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">2</span>
                <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-600 text-xs font-bold text-white">3</span>
                <span>Confirm — the app opens full-screen without browser bars.</span>
              </li>
            </ol>
          )}
          <div className="rounded-lg bg-neutral-900 p-3 text-xs text-neutral-400">
            💡 Tip: on Chrome / Edge / Samsung Internet you'll see this button prompt the install sheet directly.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
